import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Newspaper, Loader2, RefreshCw, ExternalLink, Wand2, Radio, Clock, Bookmark, Lightbulb, Layers, Trash2, PieChart, Briefcase, Baby, Users, HeartHandshake, HeartCrack, Wallet, Receipt, Stethoscope, Archive, Star, ChevronDown, ChevronRight, Tag } from "lucide-react";
import {
  getFreshNews, reactToNews, saveIdea, listSavedIdeas,
  saveNews, listSavedNews, deleteSavedNews, setNewsTema, getFunnelStats, TIPI,
} from "@/lib/api";
import { IdeaCard, TipoBadge, TIPO_COLOR } from "@/components/Ideas";
import { toast } from "sonner";

const newsKey = (item) => item.link || item.title;

// Mappa categoria-tema → etichetta "mix" mostrata accanto a ogni news
const MIX = {
  "Connessione": { label: "Connessione", color: "#6B7280" },
  "Tabù di coppia": { label: "Tabù", color: "#D92D20" },
  "Sistema": { label: "Sistema-economia", color: "#D97706" },
};

// I TEMI di Massimiliano (REGOLE_BLINDATE §0): ordine = rendimento, esempio = il reel che è andato meglio
export const TEMI_META = [
  { key: "Figli rimandati", n: 1, esempio: "Pellegrini 580k (nido: 31 posti su 100) · «Età del primo figlio» 430k · «Un figlio costa 162.000 €» record condivisioni", icon: Baby, color: "#16A34A" },
  { key: "Nonni e la rete che non c'è più", n: 2, esempio: "«I nonni lo facevano con meno» 385k", icon: Users, color: "#D97706" },
  { key: "Congedi e maternità", n: 3, esempio: "Pellegrini 580k (mamma che lavora, CON lei) · «Il padre ha 10 giorni, la madre 5 mesi» 224k · Beatrice 80k", icon: HeartHandshake, color: "#DB2777" },
  { key: "Lavoro che incatena", n: 4, esempio: "«NASpI: se ti dimetti niente disoccupazione» 94k", icon: Briefcase, color: "#2563EB" },
  { key: "Tabù di coppia", n: 5, esempio: "«Il telefono sul divano» 134k · 137 follower · 16 cond/1000 (uno a settimana, col conto dentro)", icon: HeartCrack, color: "#D92D20" },
  { key: "Soldi che non bastano", n: 6, esempio: "«Assegno unico 5 €» 22k (tiene solo con la frase di casa dentro)", icon: Wallet, color: "#9333EA" },
  { key: "Partita IVA", n: 7, esempio: "format del lunedì · «P.IVA e malattia» 9k · 214 commenti", icon: Receipt, color: "#0F766E" },
  { key: "Sanità-conto", n: 8, esempio: "in TEST (slot 18:30) · «La sanità è gratis?» → la visita di tuo figlio la paghi due volte", icon: Stethoscope, color: "#0EA5E9" },
  { key: "Fuori tema", n: null, esempio: "quello che il venerdì scarteresti: pertinente alla tesi ma senza un cassetto", icon: Archive, color: "#525252", collapsed: true },
];
const TEMA_FALLBACK = "Fuori tema";
const FORMAT_TAG_COLOR = { "Italia vs Europa": "#0EA5E9", "Non fa notizia": "#D97706", "Parole del Palazzo": "#DC2626" };
const CONTROLLI_META = [["novita", "Novità"], ["specificita", "Specificità"], ["share", "Share"], ["emozione", "Emozione"], ["frase", "Frase"]];

// Mix consigliato per categoria + risultato principale che porta
const CAT_MIX_RECO = [
  { cat: "Sistema", label: "Sistema-economia", pct: 55, esito: "Il CUORE: economia/lavoro/sistema (commenti + autorità → workshop)", color: "#D97706" },
  { cat: "Tabù di coppia", label: "Coppia ancorata al sistema", pct: 35, esito: "Coppia come CONSEGUENZA dell'economia (reach + polarizzazione)", color: "#D92D20" },
  { cat: "Connessione", label: "Speranza / via d'uscita", pct: 10, esito: "Agency: si può ripartire (rara, mai psicologia pura)", color: "#6B7280" },
];

function mixStatus(actualPct, reco) {
  if (actualPct == null) return { text: "—", color: "#6B7280" };
  const diff = actualPct - reco;
  if (Math.abs(diff) <= 7) return { text: "in linea", color: "#16A34A" };
  if (diff > 7) return { text: "troppi", color: "#D97706" };
  return { text: "pochi", color: "#2563EB" };
}

function relativeDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    const days = Math.floor(diff / 86400);
    if (days === 1) return "ieri";
    if (days < 7) return `${days} giorni fa`;
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function Attualita({ onUseIdea, onSavedChange }) {
  const [view, setView] = useState("fresh");
  const [news, setNews] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reactingKey, setReactingKey] = useState(null);
  const [tipoByKey, setTipoByKey] = useState({});
  const [ideaByKey, setIdeaByKey] = useState({});
  const [savedHooks, setSavedHooks] = useState(new Set());
  const [savedNewsKeys, setSavedNewsKeys] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [showFuori, setShowFuori] = useState(false);

  const loadStats = useCallback(async () => {
    try { setStats(await getFunnelStats()); } catch { /* ignore */ }
  }, []);

  const loadSavedIdeasSet = useCallback(async () => {
    try { const s = await listSavedIdeas(); setSavedHooks(new Set(s.map((x) => x.hook_fissato))); } catch { /* ignore */ }
  }, []);

  const loadSavedNews = useCallback(async () => {
    try {
      const s = await listSavedNews();
      setSaved(s);
      setSavedNewsKeys(new Set(s.map(newsKey)));
    } catch { /* ignore */ }
  }, []);

  const loadFresh = useCallback(async (refresh) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      let data = await getFreshNews(refresh);
      if (data.running) {
        // l'aggiornamento gira in background (~1 min): polling finché non finisce
        const deadline = Date.now() + 180000;
        while (data.running && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          data = await getFreshNews(false);
        }
      }
      if (data.error) toast.error(data.error);
      setNews(data.news || []);
      if (refresh) setIdeaByKey({});
      if (!data.news?.length && !data.error) toast.message("Nessuna notizia rilevante trovata al momento");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento delle notizie");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadFresh(false); loadSavedIdeasSet(); loadSavedNews(); loadStats(); }, [loadFresh, loadSavedIdeasSet, loadSavedNews, loadStats]);

  const onReact = async (item) => {
    const key = newsKey(item);
    setReactingKey(key);
    try {
      const idea = await reactToNews({
        title: item.title,
        summary: item.summary,
        source: item.source,
        link: item.link,
        tipo: tipoByKey[key] || item.tipo_consigliato || "Attenzione",
        categoria: item.categoria_consigliata || "",
        angolo: item.angolo_consigliato || "",
        nemico_fatto: item.nemico_fatto || "",
        hook_bozza: item.hook_bozza || "",
        frase_inoltrabile: item.frase_inoltrabile || "",
        format_mik: item.format_mik || "",
        frame: item.frame || "",
        emozione: item.emozione || "",
      });
      setIdeaByKey((prev) => ({ ...prev, [key]: idea }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione dell'angolo");
    } finally {
      setReactingKey(null);
    }
  };

  const onSaveIdea = async (idea) => {
    try {
      await saveIdea(idea);
      setSavedHooks((prev) => new Set(prev).add(idea.hook_fissato));
      toast.success("Idea salvata");
      onSavedChange?.();
      loadStats();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const onSaveNews = async (item) => {
    try {
      await saveNews({
        title: item.title,
        summary: item.summary || "",
        source: item.source || "",
        link: item.link || "",
        published: item.published || null,
        tema: item.tema || TEMA_FALLBACK,
        format_tag: item.format_tag || "",
        categoria_consigliata: item.categoria_consigliata || "Sistema",
        tipo_consigliato: item.tipo_consigliato || "Attenzione",
        angolo_consigliato: item.angolo_consigliato || "",
        protagonista_consigliato: item.protagonista_consigliato || "",
        vip: !!item.vip,
        personaggio: item.personaggio || "",
        format_mik: item.format_mik || "",
        frame: item.frame || "",
        nemico_fatto: item.nemico_fatto || "",
        hook_tipo: item.hook_tipo || "",
        hook_bozza: item.hook_bozza || "",
        emozione: item.emozione || "",
        frase_inoltrabile: item.frase_inoltrabile || "",
        controlli: item.controlli || {},
        punteggio: item.punteggio || 0,
      });
      setSavedNewsKeys((prev) => new Set(prev).add(newsKey(item)));
      toast.success("Notizia salvata in archivio");
      loadSavedNews();
    } catch {
      toast.error("Errore nel salvataggio della notizia");
    }
  };

  const onDeleteNews = async (item) => {
    try {
      await deleteSavedNews(item.id);
      toast.success("Notizia rimossa");
      loadSavedNews();
    } catch {
      toast.error("Errore nella rimozione");
    }
  };

  const onChangeTema = async (item, tema) => {
    const apply = (arr) => arr.map((x) => (x.title === item.title ? { ...x, tema } : x));
    setNews((p) => apply(p));
    setSaved((p) => apply(p));
    try {
      await setNewsTema(item.title, tema);
      toast.success(`Spostata in «${tema}»`);
    } catch {
      toast.error("Errore nel cambio tema");
    }
  };

  const NewsCard = ({ item, idx, isSavedView }) => {
    const key = newsKey(item);
    const tipoVal = tipoByKey[key] ?? item.tipo_consigliato ?? "Attenzione";
    const isBookmarked = savedNewsKeys.has(key);
    const testPrefix = isSavedView ? "saved-news" : "news";
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(idx, 8) * 0.04 }}
        className="bg-[#121212] border border-[#262626] rounded-md p-5"
        data-testid={`${testPrefix}-card-${idx}`}
      >
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm text-[#D97706]" style={{ background: "#D9770622" }}>
            {item.source}
          </span>
          {item.vip && (
            <span
              className="text-[0.6rem] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm flex items-center gap-1"
              style={{ background: "#EAB30822", color: "#EAB308" }}
              data-testid={`news-vip-${idx}`}
              title={item.personaggio ? `Dichiarazione di ${item.personaggio}` : "Dichiarazione di un personaggio pubblico"}
            >
              <Star size={9} /> Reazione VIP{item.personaggio ? ` · ${item.personaggio}` : ""}
            </span>
          )}
          {item.format_tag && (
            <span
              className="text-[0.6rem] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm flex items-center gap-1"
              style={{ background: `${FORMAT_TAG_COLOR[item.format_tag] || "#525252"}22`, color: FORMAT_TAG_COLOR[item.format_tag] || "#a3a3a3" }}
              data-testid={`${testPrefix}-format-${idx}`}
              title="Etichetta di format (non è un tema)"
            >
              <Tag size={9} /> {item.format_tag}
            </span>
          )}
          {item.published && (
            <span className="text-xs text-neutral-600 flex items-center gap-1">
              <Clock size={11} /> {relativeDate(item.published)}
            </span>
          )}
          {item.categoria_consigliata && MIX[item.categoria_consigliata] && (
            <span
              className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm"
              style={{ background: `${MIX[item.categoria_consigliata].color}22`, color: MIX[item.categoria_consigliata].color }}
              data-testid={`news-mix-${idx}`}
            >
              {MIX[item.categoria_consigliata].label}
            </span>
          )}
          {item.tipo_consigliato && (
            <span className="text-xs text-neutral-500 flex items-center gap-1.5">
              Consigliato: <TipoBadge tipo={item.tipo_consigliato} />
            </span>
          )}
          <div className="ml-auto">
            {isSavedView ? (
              <button
                data-testid={`delete-saved-news-${idx}`}
                onClick={() => onDeleteNews(item)}
                className="text-neutral-500 hover:text-[#D92D20] transition-colors"
                title="Rimuovi dall'archivio"
              >
                <Trash2 size={15} />
              </button>
            ) : (
              <button
                data-testid={`save-news-${idx}`}
                onClick={() => onSaveNews(item)}
                className={`transition-colors ${isBookmarked ? "text-[#D92D20]" : "text-neutral-500 hover:text-white"}`}
                title={isBookmarked ? "Già in archivio" : "Salva la notizia in archivio"}
              >
                <Bookmark size={15} fill={isBookmarked ? "#D92D20" : "none"} />
              </button>
            )}
          </div>
        </div>

        <p className="font-display font-bold text-lg leading-snug text-white mb-1">{item.title}</p>
        {item.summary && <p className="text-sm text-neutral-400 mb-3 leading-relaxed">{item.summary}</p>}

        {(item.angolo_consigliato || item.nemico_fatto || item.hook_bozza || item.controlli) && (
          <div className="mb-3 rounded-sm border border-[#262626] bg-[#0F0F0F] px-3 py-2.5 space-y-2" data-testid={`${testPrefix}-metodo-${idx}`}>
            <div className="flex flex-wrap items-center gap-1.5">
              {item.format_mik && <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#1a1a1a] border border-[#262626] text-neutral-300">{item.format_mik}</span>}
              {item.frame && <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#1a1a1a] border border-[#262626] text-neutral-300">Frame {item.frame}{item.frame === "03" ? " · CTA" : " · senza CTA"}</span>}
              {item.emozione && <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#1a1a1a] border border-[#262626] text-neutral-300">{item.emozione}</span>}
              {item.hook_tipo && <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{ color: item.hook_tipo === "Citazione" ? "#EAB308" : "#a3a3a3", borderColor: item.hook_tipo === "Citazione" ? "#EAB30855" : "#262626" }}>hook {item.hook_tipo}</span>}
              {item.controlli && (
                <span className="ml-auto flex items-center gap-2" data-testid={`${testPrefix}-controlli-${idx}`} title="I 5 controlli anti-flop">
                  {CONTROLLI_META.map(([k, l]) => {
                    const ok = item.controlli[k];
                    return <span key={k} className="text-[0.62rem] font-semibold" style={{ color: ok === true ? "#16A34A" : ok === false ? "#D92D20" : "#404040" }}>{ok === true ? "✔" : ok === false ? "✗" : "–"} {l}</span>;
                  })}
                </span>
              )}
            </div>
            {item.hook_bozza && <p className="text-[0.72rem] font-mono uppercase text-neutral-100 leading-snug border-l-2 pl-2" style={{ borderColor: "#D97706" }} data-testid={`${testPrefix}-hook-${idx}`}>{item.hook_bozza}</p>}
            {item.angolo_consigliato && (
              <p className="text-[0.8rem] text-neutral-300 leading-snug flex items-start gap-2" data-testid={`${testPrefix}-angolo-${idx}`}>
                <Lightbulb size={13} className="text-[#D92D20] mt-0.5 shrink-0" />
                <span>
                  <span className="text-neutral-500">Angolo:</span> {item.angolo_consigliato}
                  {item.protagonista_consigliato && (
                    <span className="block text-neutral-500 mt-0.5">Protagonista: {item.protagonista_consigliato}</span>
                  )}
                </span>
              </p>
            )}
            {item.nemico_fatto && <p className="text-xs text-neutral-400 leading-snug"><span className="text-neutral-500">Nemico-fatto:</span> {item.nemico_fatto}</p>}
            {item.frase_inoltrabile && <p className="text-xs italic text-neutral-300 leading-snug">«{item.frase_inoltrabile}»</p>}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-neutral-500 hover:text-neutral-300 flex items-center gap-1 transition-colors"
              data-testid={`${testPrefix}-link-${idx}`}
            >
              <ExternalLink size={12} /> Leggi l'articolo
            </a>
          )}
          <div className="flex items-center gap-1.5 ml-auto flex-wrap justify-end">
            <select
              data-testid={`${testPrefix}-tema-${idx}`}
              value={item.tema || TEMA_FALLBACK}
              onChange={(e) => onChangeTema(item, e.target.value)}
              title="Correggi il tema"
              className="bg-[#0A0A0A] border border-[#262626] rounded-sm text-xs text-neutral-300 py-1.5 px-2 focus:outline-none focus:border-[#D92D20] max-w-[190px]"
            >
              {TEMI_META.map((t) => (
                <option key={t.key} value={t.key}>{t.n ? `${t.n} · ` : ""}{t.key}</option>
              ))}
            </select>
            <select
              data-testid={`${testPrefix}-tipo-${idx}`}
              value={tipoVal}
              onChange={(e) => setTipoByKey((p) => ({ ...p, [key]: e.target.value }))}
              className="bg-[#0A0A0A] border border-[#262626] rounded-sm text-xs text-neutral-300 py-1.5 px-2 focus:outline-none focus:border-[#D92D20]"
            >
              {TIPI.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              data-testid={`react-${testPrefix}-${idx}`}
              onClick={() => onReact(item)}
              disabled={reactingKey === key}
              className="bg-[#D92D20] hover:bg-[#B42318] disabled:opacity-60 text-white font-bold py-2 px-4 rounded-sm flex items-center gap-2 transition-colors text-sm"
            >
              {reactingKey === key ? <Loader2 className="animate-spin" size={15} /> : <Wand2 size={15} />}
              {ideaByKey[key] ? "Rigenera angolo" : "Trasforma in reel"}
            </button>
          </div>
        </div>

        {ideaByKey[key] && (
          <div className="mt-4 pt-4 border-t border-[#262626]" data-testid={`${testPrefix}-idea-${idx}`}>
            <IdeaCard
              idea={ideaByKey[key]}
              i={idx}
              onUseIdea={onUseIdea}
              onSave={onSaveIdea}
              isSaved={savedHooks.has(ideaByKey[key].hook_fissato)}
            />
          </div>
        )}
      </motion.div>
    );
  };

  const list = view === "fresh" ? news : saved;
  const catTotal = stats ? CAT_MIX_RECO.reduce((s, r) => s + (stats.categorie?.[r.cat] || 0), 0) : 0;

  // raggruppa le notizie per TEMA (mantenendo l'indice globale per i data-testid).
  // Gli 8 temi si vedono SEMPRE (anche vuoti: così il cassetto scoperto salta all'occhio); "Fuori tema" solo se ha notizie, chiuso di default
  const grouped = TEMI_META
    .map((t) => ({
      ...t,
      items: list.map((item, i) => ({ item, i })).filter(({ item }) => (item.tema || TEMA_FALLBACK) === t.key),
    }))
    .filter((g) => g.items.length > 0 || (!g.collapsed && view === "fresh"));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Radio size={22} className="text-[#D92D20]" />
            <h2 className="font-display font-extrabold text-3xl tracking-tight">Attualità</h2>
          </div>
          <p className="text-sm text-neutral-500 mt-2 max-w-xl">
            Un solo posto per le idee: ogni notizia finisce nel <span className="text-neutral-300">tuo tema</span> (tema validato × notizia fresca × frase di casa), col <span className="text-neutral-300">nemico-fatto</span> e i <span className="text-neutral-300">5 controlli anti-flop</span>. Dentro ci sono anche le fonti di Italia vs Europa, Non fa notizia e Partita IVA (etichetta di format). Se un tema è sbagliato, correggilo dalla tendina sulla card.
          </p>
        </div>
        <button
          data-testid="refresh-news-btn"
          onClick={() => loadFresh(true)}
          disabled={refreshing || loading}
          className="self-start sm:self-auto border border-[#333] hover:border-[#D92D20] text-neutral-300 hover:text-white font-semibold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm disabled:opacity-60"
        >
          {refreshing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          Aggiorna news
        </button>
      </div>

      {/* Contatore di equilibrio del funnel */}
      {stats && (
        <div className="flex items-center gap-2 flex-wrap mb-4 text-xs" data-testid="funnel-balance">
          <span className="text-neutral-500 flex items-center gap-1.5"><Layers size={13} /> Funnel (tipo):</span>
          {TIPI.map((t) => (
            <span
              key={t}
              className="px-2 py-1 rounded-sm border font-semibold"
              style={{ color: TIPO_COLOR[t], borderColor: `${TIPO_COLOR[t]}66` }}
              data-testid={`balance-${t}`}
            >
              {t} <b className="text-white">{stats.totale?.[t] ?? 0}</b>
            </span>
          ))}
        </div>
      )}

      {/* Mix di contenuti: consigliato vs fatto */}
      {stats && (
        <div className="mb-6 bg-[#121212] border border-[#262626] rounded-md p-4" data-testid="mix-panel">
          <div className="flex items-center gap-1.5 mb-3 text-xs text-neutral-500">
            <PieChart size={13} /> Mix di contenuti — consigliato vs fatto
            <span className="text-neutral-600">({catTotal} script)</span>
          </div>
          <div className="space-y-3.5">
            {CAT_MIX_RECO.map(({ cat, label, pct, esito, color }) => {
              const count = stats.categorie?.[cat] || 0;
              const actualPct = catTotal > 0 ? Math.round((count / catTotal) * 100) : null;
              const st = mixStatus(actualPct, pct);
              return (
                <div key={cat} data-testid={`mix-row-${cat}`}>
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-x-2 gap-y-0.5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
                      <span className="text-[11px] text-neutral-500">· {esito}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-neutral-500">consigliato <b className="text-neutral-300">{pct}%</b></span>
                      <span className="text-neutral-700">·</span>
                      <span className="text-neutral-500">fatto <b className="text-white" data-testid={`mix-actual-${cat}`}>{actualPct == null ? "—" : actualPct + "%"}</b></span>
                      <span className="font-semibold px-1.5 py-0.5 rounded-sm" style={{ color: st.color, background: `${st.color}1A` }}>{st.text}</span>
                    </div>
                  </div>
                  <div className="relative h-2 rounded-full bg-[#0A0A0A] overflow-hidden">
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${actualPct || 0}%`, background: color }} />
                    <div className="absolute inset-y-[-2px] w-0.5 bg-white" style={{ left: `calc(${pct}% - 1px)` }} title={`Consigliato ${pct}%`} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-neutral-600 mt-3">La linea bianca è la % consigliata; la barra colorata è quella che hai fatto finora.</p>
        </div>
      )}

      {/* Toggle Fresche / Salvate */}
      <div className="flex gap-1 mb-6">
        <button
          data-testid="view-fresh"
          onClick={() => setView("fresh")}
          className={`text-sm font-semibold py-2 px-4 rounded-sm border transition-colors ${
            view === "fresh" ? "bg-[#D92D20] border-[#D92D20] text-white" : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
          }`}
        >
          Fresche
        </button>
        <button
          data-testid="view-saved"
          onClick={() => setView("saved")}
          className={`text-sm font-semibold py-2 px-4 rounded-sm border transition-colors flex items-center gap-1.5 ${
            view === "saved" ? "bg-[#D92D20] border-[#D92D20] text-white" : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
          }`}
        >
          <Bookmark size={13} /> Salvate ({saved.length})
        </button>
      </div>

      {view === "fresh" && loading ? (
        <div className="border border-[#262626] rounded-md min-h-[260px] flex flex-col items-center justify-center">
          <Loader2 className="animate-spin text-[#D92D20] mb-4" size={30} />
          <p className="text-neutral-400">Cerco e valuto le notizie nei tuoi temi… (circa un minuto)</p>
        </div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md min-h-[200px] flex flex-col items-center justify-center text-center p-8">
          <Newspaper size={34} className="text-neutral-700 mb-3" />
          <p className="text-neutral-500">
            {view === "fresh" ? "Nessuna notizia rilevante al momento. Riprova più tardi." : "Nessuna notizia salvata. Usa il segnalibro sulle news fresche per tenerle qui."}
          </p>
        </div>
      ) : (
        <div className="space-y-8" data-testid={view === "fresh" ? "news-list" : "saved-news-list"}>
          {grouped.map((g) => {
            const Icon = g.icon;
            const open = !g.collapsed || showFuori;
            return (
              <section key={g.key} data-testid={`tema-${g.key}`}>
                <div className="flex items-center gap-2 mb-3 sticky top-[72px] z-10 bg-[#0A0A0A]/90 backdrop-blur py-1">
                  <span className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${g.color}22`, color: g.color }}>
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-lg tracking-tight leading-tight">
                      {g.n && <span className="text-neutral-500 mr-1.5">{g.n}</span>}{g.key}
                      <span className="text-xs text-neutral-600 font-semibold ml-2">{g.items.length}</span>
                    </h3>
                    <p className="text-[11px] text-neutral-500 truncate" data-testid={`tema-esempio-${g.key}`}>({g.esempio})</p>
                  </div>
                  <span className="flex-1 h-px bg-[#262626] ml-2" />
                  {g.collapsed && (
                    <button
                      data-testid="toggle-fuori-tema"
                      onClick={() => setShowFuori((v) => !v)}
                      className="text-xs text-neutral-400 hover:text-white flex items-center gap-1 border border-[#333] rounded-sm px-2 py-1 transition-colors"
                    >
                      {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {open ? "Nascondi" : "Mostra"}
                    </button>
                  )}
                </div>
                {open && g.items.length === 0 && (
                  <p className="text-xs text-neutral-600 italic pl-9" data-testid={`tema-vuoto-${g.key}`}>
                    Oggi nessuna notizia in questo cassetto{g.key === "Tabù di coppia" ? " — normale: il Tabù non nasce dai giornali, nasce da una frase di casa più un conto. Prendi il dato da un altro tema e portalo sul divano." : "."}
                  </p>
                )}
                {open && g.items.length > 0 && (
                  <div className="space-y-4">
                    {g.items.map(({ item, i }) => (
                      <NewsCard key={`${view}-${newsKey(item)}`} item={item} idx={i} isSavedView={view === "saved"} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
